import { CART_QUERY_NAME } from '@pylot-data/core-queries/cart'
import type { GraphQLError } from '@pylot-data/graphqlFetch'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { SwrUseQueryOutput, useQuery } from '@pylot-data/hooks/use-query'
import type { Cart, CartUserInputError, Maybe } from '@pylot-data/pylotschema'
import type { RequestInit } from '@vercel/fetch'
import { useRouter } from 'next/router'
import type { SWRConfiguration } from 'swr'
import { CART_QUERY, CUSTOMER_CART_QUERY } from './graphql/cartQuery'
import { getIsSignedIn } from './utils/getIsSignedIn'
import { setShippingAddressesMutation } from '@corratech/pylot-checkout-set-cart-address/src/graphql/setShippingAddressMutation'
import { useUser } from '@corratech/pylot-auth-manager'

export type CartData = {
    data: {
        cart: Cart
        isOptimistic?: boolean
    }
    errors?: GraphQLError[]
    user_errors?: Maybe<CartUserInputError>[]
}

export const useCart = (
    swrOptions?: SWRConfiguration,
    fetchOptions?: RequestInit
): SwrUseQueryOutput<{
    cart: Cart
    isOptimistic?: boolean
}> => {
    const { locale } = useRouter()
    const { customer } = useUser()
    // @ts-ignore We need this override so that we can use 2 different queries for the same query key
    const cartFetcher: typeof graphqlFetch = (
        ignoredKey: string,
        ignoredVariables: Record<string, any>,
        fetchOptions: RequestInit
    ) => {
        const isSignedIn = getIsSignedIn()
        const cartId = window.localStorage.getItem('cart_id') ?? ''
        const variables = isSignedIn ? { isSignedIn: true } : { cartId }
        const selectedQuery = isSignedIn ? CUSTOMER_CART_QUERY : CART_QUERY
        return graphqlFetch({
            query: selectedQuery,
            variables,
            fetchOptions,
            locale
        })
    }

    const response = useQuery<{ cartId: string } | null, { cart: Cart }>(
        CART_QUERY_NAME,
        null,
        {
            ...swrOptions,
            onSuccess: (result, key, config) => {
                const isSignedIn = getIsSignedIn()
                const cartId = window.localStorage.getItem('cart_id')
                if (isSignedIn && cartId !== result.data.cart.id) {
                    const addresses = customer?.addresses ?? []
                    const shippingAddress = addresses.find(
                        (res) => res?.default_shipping
                    )
                    window.localStorage.setItem('cart_id', result.data.cart.id)
                    return graphqlFetch({
                        query: setShippingAddressesMutation,
                        variables: {
                            input: {
                                cart_id: result.data.cart.id,
                                shipping_addresses: [
                                    {
                                        customer_address_id: shippingAddress?.id
                                    }
                                ]
                            },
                            isSignedIn
                        },
                        locale
                    })
                }
                swrOptions?.onSuccess?.(result, key, config)
            }
        },
        fetchOptions,
        {
            overrideFetcher: cartFetcher
        }
    )

    return {
        ...response,
        isValidating: response?.isValidating
    }
}
